import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Trophy, Calendar, Users, Activity, Edit, MapPin,
    Flag, Clock, ChevronRight, Plus, Trash2, Save, X, Crown, Shield, Video,
    Image, Film, MessageCircle, Heart, Camera, Star, UserPlus, Check, ArrowLeft
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { teamProfileAPI, rosterAPI, videosAPI } from '../services/api';
import { socialAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import SimpleVideoPlayer from '../components/SimpleVideoPlayer';
import ExpandableText from '../components/ExpandableText';
import { cn } from '../lib/utils';

// Tabs Component
const Tabs = ({ activeTab, onTabChange, isCoach }) => (
    <div className="flex bg-black/40 backdrop-blur-md p-1.5 rounded-[1.5rem] border border-white/5 mb-8 w-full max-w-sm mx-auto shadow-xl ring-1 ring-white/5">
        {['overview', 'calendar', 'roster'].map((tab) => (
            <button
                key={tab}
                onClick={() => onTabChange(tab)}
                className={cn(
                    "flex-1 py-3 text-sm font-black capitalize rounded-[1.2rem] transition-all duration-300 relative overflow-hidden",
                    activeTab === tab
                        ? "text-white bg-gradient-to-br from-snapp-purple to-snapp-blue shadow-lg shadow-snapp-purple/20"
                        : "text-gray-500 hover:text-gray-300"
                )}
            >
                <div className="relative z-10">{tab}</div>
                {activeTab === tab && (
                    <div className="absolute inset-0 bg-white/10 animate-in fade-in zoom-in duration-500" />
                )}
            </button>
        ))}
    </div>
);

const CreatePostModal = ({ teamId, isOpen, onClose, initialType = 'text', onPostCreated }) => {
    const { user } = useAuth();
    const { toast } = useToast();
    const [caption, setCaption] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const fileInputImageRef = useRef(null);
    const fileInputVideoRef = useRef(null);

    // Initialize based on type
    useEffect(() => {
        if (isOpen) {
            setCaption('');
            setUploadFile(null);

            // Auto-trigger file selection if media type
            if (initialType === 'photo' && fileInputImageRef.current) {
                fileInputImageRef.current.click();
            } else if (initialType === 'video' && fileInputVideoRef.current) {
                fileInputVideoRef.current.click();
            }
        }
    }, [isOpen, initialType]);

    const handleCreatePost = async () => {
        if (!caption && !uploadFile) return;

        try {
            setLoading(true);
            let mediaData = {};

            if (uploadFile) {
                const formData = new FormData();
                formData.append('file', uploadFile.file);

                if (uploadFile.type === 'image') {
                    const response = await socialAPI.uploadImage(formData);
                    mediaData.image_url = response.data.url;
                } else if (uploadFile.type === 'video') {
                    const response = await socialAPI.uploadVideo(formData);
                    mediaData.video_id = response.data.video_id;
                }
            }

            await teamProfileAPI.createPost(teamId, {
                caption,
                ...mediaData
            });

            toast({ title: "Post shared!" });
            onPostCreated();
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to share post"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        const preview = URL.createObjectURL(file);
        setUploadFile({ file, type, preview });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-[100] p-4 pt-[10vh]">
            <div className="bg-[#1a1a1b] border border-gray-800 rounded-xl w-full max-w-lg shadow-2xl relative overflow-hidden ring-1 ring-white/10">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-800/50 bg-white/5">
                    <h3 className="font-semibold text-white">Create Post</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="flex gap-3">
                        <Avatar className="h-10 w-10 ring-2 ring-gray-800">
                            <AvatarImage src={user?.avatar_url} />
                            <AvatarFallback className="bg-blue-600">
                                {user?.username?.substring(0, 2).toUpperCase() || 'U'}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-2">
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-white">{user?.username}</span>
                                <span className="text-xs text-blue-400 flex items-center gap-1">
                                    <Shield className="w-3 h-3" /> Posting to Team Feed
                                </span>
                            </div>
                            <textarea
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                placeholder="What's happening with the team?"
                                className="w-full bg-transparent border-none text-white placeholder:text-gray-500 focus:ring-0 resize-none min-h-[120px] text-base p-0 mt-2"
                                autoFocus={initialType === 'text'}
                            />
                        </div>
                    </div>

                    {/* Hidden Inputs */}
                    <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        ref={fileInputImageRef}
                        onChange={(e) => handleFileSelect(e, 'image')}
                    />
                    <input
                        type="file"
                        className="hidden"
                        accept="video/*"
                        ref={fileInputVideoRef}
                        onChange={(e) => handleFileSelect(e, 'video')}
                    />

                    {/* Media Preview */}
                    {uploadFile && (
                        <div className="relative rounded-xl overflow-hidden border border-gray-800 bg-black aspect-video group">
                            {uploadFile.type === 'video' ? (
                                <video src={uploadFile.preview} className="w-full h-full object-cover" controls />
                            ) : (
                                <img src={uploadFile.preview} className="w-full h-full object-cover" alt="Preview App" />
                            )}
                            <button
                                onClick={() => setUploadFile(null)}
                                className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 backdrop-blur-md rounded-full text-white transition-all"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-800/50">
                        <div className="flex gap-1">
                            <button
                                onClick={() => fileInputImageRef.current?.click()}
                                className="p-2.5 hover:bg-blue-500/10 rounded-full text-blue-400 transition-colors group"
                                title="Add Photo"
                            >
                                <Image className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </button>
                            <button
                                onClick={() => fileInputVideoRef.current?.click()}
                                className="p-2.5 hover:bg-blue-500/10 rounded-full text-blue-400 transition-colors group"
                                title="Add Video"
                            >
                                <Film className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </button>
                        </div>
                        <Button
                            onClick={handleCreatePost}
                            disabled={loading || (!caption && !uploadFile)}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 rounded-full"
                        >
                            {loading ? 'Posting...' : 'Post'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Overview Tab
const OverviewTab = ({ team, isCoach, canManageProfile, onUpdate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { toast } = useToast();
    const [editing, setEditing] = useState(false);

    // Create Post State
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [createType, setCreateType] = useState('text'); // 'text', 'photo', 'video'

    const canPost = isCoach || team.is_captain || (team.permissions && team.permissions.includes('manage_posts')) || user?.role === 'admin';

    const [formData, setFormData] = useState({
        bio: team.bio || '',
        founded_year: team.founded_year || '',
        home_field: team.home_field || '',
        achievements: team.achievements_text || ''
    });

    const [posts, setPosts] = useState([]);
    const [loadingPosts, setLoadingPosts] = useState(true);

    const fetchPosts = async () => {
        try {
            const response = await teamProfileAPI.getPosts(team.id);
            setPosts(response.data);
        } catch (error) {
            console.error('Failed to load posts:', error);
        } finally {
            setLoadingPosts(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, [team.id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await teamProfileAPI.updateProfile(team.id, formData);
            toast({
                title: "Success",
                description: "Profile updated successfully",
            });
            setEditing(false);
            onUpdate();
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to update profile",
            });
        }
    };

    const handleOpenCreate = (type) => {
        setCreateType(type);
        setCreateModalOpen(true);
    };

    if (editing) {
        return (
            <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl bg-[#1a1a1b] p-6 rounded-lg border border-gray-800">
                <h3 className="text-lg font-semibold text-white mb-4">Edit Team Details</h3>
                <div>
                    <label className="block text-sm text-gray-400 mb-1">Team Bio</label>
                    <textarea
                        value={formData.bio}
                        onChange={e => setFormData({ ...formData, bio: e.target.value })}
                        className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white h-24"
                        placeholder="Tell us about your team..."
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Founded Year</label>
                        <input
                            type="text"
                            value={formData.founded_year}
                            onChange={e => setFormData({ ...formData, founded_year: e.target.value })}
                            className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                            placeholder="e.g. 2020"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Home Field</label>
                        <input
                            type="text"
                            value={formData.home_field}
                            onChange={e => setFormData({ ...formData, home_field: e.target.value })}
                            className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                            placeholder="Stadium Name"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm text-gray-400 mb-1">Achievements Summary</label>
                    <textarea
                        value={formData.achievements}
                        onChange={e => setFormData({ ...formData, achievements: e.target.value })}
                        className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white h-20"
                        placeholder="List your major achievements..."
                    />
                </div>
                <div className="flex gap-2 justify-end">
                    <button
                        type="button"
                        onClick={() => setEditing(false)}
                        className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Save Changes
                    </button>
                </div>
            </form>
        );
    }

    return (
        <div className="space-y-6">
            <CreatePostModal
                teamId={team.id}
                isOpen={isCreateModalOpen}
                onClose={() => setCreateModalOpen(false)}
                initialType={createType}
                onPostCreated={fetchPosts}
            />

            <div className="space-y-6">
                <div className="bg-[#1a1a1b]/60 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/5 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-snapp-purple/10 blur-[50px] -mr-16 -mt-16 group-hover:bg-snapp-purple/20 transition-colors" />

                    {canManageProfile && (
                        <button
                            onClick={() => setEditing(true)}
                            className="absolute top-6 right-6 p-3 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-2xl transition-all active:scale-90"
                        >
                            <Edit className="w-5 h-5" />
                        </button>
                    )}

                    <div className="space-y-8">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-snapp-blue/10 rounded-xl">
                                    <Activity className="w-5 h-5 text-snapp-blue" />
                                </div>
                                <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('profile.aboutUs', 'Hakkımızda')}</h3>
                            </div>
                            <p className="text-gray-400 leading-relaxed text-base font-medium">
                                {team.bio || "No description provided yet."}
                            </p>
                        </div>

                        {team.achievements_text && (
                            <div className="pt-6 border-t border-white/5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-amber-500/10 rounded-xl">
                                        <Trophy className="w-5 h-5 text-amber-500" />
                                    </div>
                                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('profile.achievements', 'Başarılar')}</h3>
                                </div>
                                <div className="p-4 bg-white/5 rounded-2xl border border-white/5 italic text-gray-300 font-medium">
                                    "{team.achievements_text}"
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Team Feed Section */}
            <div className="mt-16">
                <div className="flex items-center justify-between mb-8 px-2">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-snapp-purple/10 rounded-2xl">
                            <MessageCircle className="w-6 h-6 text-snapp-purple" />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('social.teamFeed', 'Takım Akışı')}</h3>
                            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">{t('social.latestUpdates', 'Son Güncellemeler')}</p>
                        </div>
                    </div>

                    {/* Add Post Button */}
                    {canPost && (
                        <button
                            onClick={() => handleOpenCreate('text')}
                            className="bg-white/5 hover:bg-white/10 text-white p-3 rounded-2xl transition-all active:scale-95 shadow-lg border border-white/5"
                        >
                            <Plus className="w-6 h-6" />
                        </button>
                    )}
                </div>

                {loadingPosts ? (
                    <div className="flex justify-center py-12">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 bg-[#1a1a1b]/30 rounded-lg border border-gray-800 border-dashed">
                        <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-10" />
                        <p className="text-sm">No posts from the team yet.</p>
                    </div>
                ) : (
                    <div className="max-w-xl mx-auto space-y-4">
                        {posts.map(post => (
                            <PostCard key={post.id} post={post} allowViewAuthor={canPost} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Trophies Tab
const TrophiesTab = ({ team, trophies, isCoach, onUpdate }) => {
    const { toast } = useToast();
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTrophy, setNewTrophy] = useState({ title: '', year: new Date().getFullYear(), description: '' });

    const handleAddTrophy = async (e) => {
        e.preventDefault();
        try {
            await teamProfileAPI.addTrophy(team.id, newTrophy);
            toast({
                title: "Success",
                description: "Trophy added!",
            });
            setShowAddModal(false);
            setNewTrophy({ title: '', year: new Date().getFullYear(), description: '' });
            onUpdate();
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to add trophy",
            });
        }
    };

    const handleDelete = async (trophyId) => {
        if (!window.confirm('Are you sure you want to remove this trophy?')) return;
        try {
            await teamProfileAPI.deleteTrophy(team.id, trophyId);
            toast({
                title: "Success",
                description: "Trophy removed",
            });
            onUpdate();
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to remove trophy",
            });
        }
    };

    return (
        <div className="space-y-6">
            {isCoach && (
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                    <Plus className="w-4 h-4" /> Add Trophy
                </button>
            )}

            {trophies.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-[#1a1a1b] rounded-lg border border-gray-800">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No trophies in the showcase yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {trophies.map((trophy) => (
                        <div key={trophy.id} className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-6 relative group hover:border-blue-500/50 transition-colors">
                            {isCoach && (
                                <button
                                    onClick={() => handleDelete(trophy.id)}
                                    className="absolute top-3 right-3 p-2 text-gray-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-full flex items-center justify-center mb-4 border border-yellow-500/30">
                                    <Trophy className="w-8 h-8 text-yellow-500" />
                                </div>
                                <h3 className="font-bold text-white text-lg">{trophy.title}</h3>
                                <span className="text-sm text-blue-400 font-mono mb-2">{trophy.year}</span>
                                <p className="text-sm text-gray-400">{trophy.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold text-white mb-4">Add Trophy</h3>
                        <form onSubmit={handleAddTrophy} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Title</label>
                                <input
                                    required
                                    value={newTrophy.title}
                                    onChange={e => setNewTrophy({ ...newTrophy, title: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                    placeholder="Championship Name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Year</label>
                                <input
                                    type="number"
                                    required
                                    value={newTrophy.year}
                                    onChange={e => setNewTrophy({ ...newTrophy, year: parseInt(e.target.value) })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Description</label>
                                <textarea
                                    value={newTrophy.description}
                                    onChange={e => setNewTrophy({ ...newTrophy, description: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white h-24"
                                    placeholder="Details about the achievement..."
                                />
                            </div>
                            <div className="flex gap-2 justify-end mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-gray-300 hover:bg-gray-800 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Add Trophy
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Calendar Tab
const CalendarTab = ({ team, events, isCoach, onUpdate }) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [showEventModal, setShowEventModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [eventForm, setEventForm] = useState({
        event_type: 'training',
        title: '',
        opponent: '',
        venue: '',
        event_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        notes: '',
        result: ''
    });

    const openCreateModal = () => {
        setEditingEvent(null);
        setEventForm({
            event_type: 'training',
            title: '',
            opponent: '',
            venue: '',
            event_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
            notes: '',
            result: ''
        });
        setShowEventModal(true);
    };

    const openEditModal = (event) => {
        setEditingEvent(event.id);
        const backendEventDate = event.event_date ? new Date(event.event_date) : new Date();
        const adjustedDate = new Date(backendEventDate.getTime() - backendEventDate.getTimezoneOffset() * 60000);
        setEventForm({
            event_type: event.event_type || 'training',
            title: event.title || '',
            opponent: event.opponent || '',
            venue: event.venue || '',
            event_date: adjustedDate.toISOString().slice(0, 16),
            notes: event.notes || '',
            result: event.result || ''
        });
        setShowEventModal(true);
    };

    const handleSubmitEvent = async (e) => {
        e.preventDefault();
        try {
            const dateObj = new Date(eventForm.event_date);
            const payload = {
                ...eventForm,
                event_date: dateObj.toISOString()
            };
            if (!payload.opponent) payload.opponent = null;
            if (!payload.venue) payload.venue = null;
            if (!payload.notes) payload.notes = null;

            if (editingEvent) {
                await teamProfileAPI.updateEvent(team.id, editingEvent, payload);
                toast({ title: "Success", description: "Event updated successfully!" });
            } else {
                await teamProfileAPI.addEvent(team.id, payload);
                toast({ title: "Success", description: "Event created successfully!" });
            }
            setShowEventModal(false);
            if (onUpdate) onUpdate();
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.detail || "Failed to save event.",
                variant: "destructive"
            });
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm("Are you sure you want to delete this event?")) return;
        try {
            await teamProfileAPI.deleteEvent(team.id, eventId);
            toast({ title: "Success", description: "Event deleted." });
            if (onUpdate) onUpdate();
        } catch (error) {
            toast({ title: "Error", description: "Failed to delete event.", variant: "destructive" });
        }
    };

    return (
        <div className="space-y-6">
            {isCoach && (
                <button
                    onClick={openCreateModal}
                    className="flex items-center gap-2 bg-snapp-purple hover:bg-snapp-purple/80 text-white px-4 py-2 rounded-lg text-sm transition-colors mb-4 font-bold max-w-[200px]"
                >
                    <Plus className="w-4 h-4" /> Add Event (Training)
                </button>
            )}

            <div className="space-y-4">
                {events.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 bg-[#1a1a1b] rounded-lg border border-gray-800">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p>No upcoming events scheduled.</p>
                    </div>
                ) : (
                    events.map((event) => (
                        <div key={event.id} className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-4 flex items-center justify-between group relative">
                            {isCoach && event.id && (
                                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => openEditModal(event)} className="p-1.5 bg-blue-500/10 text-blue-500 hover:bg-blue-500 hover:text-white rounded-md">
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDeleteEvent(event.id)} className="p-1.5 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded-md">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            <div className="flex items-center gap-4">
                                <div className={cn(
                                    "w-12 h-12 rounded-lg flex flex-col items-center justify-center border",
                                    event.status === 'completed'
                                        ? "bg-green-500/10 border-green-500/30 text-green-500"
                                        : "bg-red-500/10 border-red-500/30 text-red-500"
                                )}>
                                    <span className="text-xs font-bold uppercase">{event.event_date ? format(parseISO(event.event_date), 'MMM') : 'TBD'}</span>
                                    <span className="font-bold text-lg">{event.event_date ? format(parseISO(event.event_date), 'd') : '?'}</span>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={cn(
                                            "text-[10px] uppercase font-bold px-2 py-0.5 rounded",
                                            event.status === 'completed'
                                                ? "bg-green-500/20 text-green-400"
                                                : "bg-red-500/20 text-red-400"
                                        )}>
                                            {event.status === 'completed' ? 'FINISH' : (event.event_type || 'EVENT').toUpperCase()}
                                        </span>
                                        {event.event_date && (
                                            <span className="text-sm text-gray-400 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {format(parseISO(event.event_date), 'HH:mm')}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-bold text-white text-lg">
                                        {event.title}
                                    </h3>
                                    {event.opponent && (
                                        <p className="text-sm text-gray-400">vs {event.opponent}</p>
                                    )}
                                    {event.venue && (
                                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                            <MapPin className="w-3 h-3" /> {event.venue}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {event.status === 'completed' && event.score_a !== undefined && (
                                <div className="text-right">
                                    <div className="text-2xl font-black text-white tabular-nums">
                                        {event.score_a} - {event.score_b}
                                    </div>
                                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Final Score</div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {showEventModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold text-white mb-4">{editingEvent ? 'Edit Event' : 'Add Event (Training)'}</h3>
                        <form onSubmit={handleSubmitEvent} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Type</label>
                                <select
                                    value={eventForm.event_type}
                                    onChange={e => setEventForm({ ...eventForm, event_type: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                    required
                                >
                                    <option value="training">Training</option>
                                    <option value="match">Match</option>
                                    <option value="tournament">Tournament</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Title</label>
                                <input
                                    required
                                    value={eventForm.title}
                                    onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                    placeholder="e.g. Offensive Training"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Date & Time</label>
                                <input
                                    type="datetime-local"
                                    required
                                    value={eventForm.event_date}
                                    onChange={e => setEventForm({ ...eventForm, event_date: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Venue (Optional)</label>
                                <input
                                    value={eventForm.venue}
                                    onChange={e => setEventForm({ ...eventForm, venue: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                    placeholder="Location"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Opponent (Optional)</label>
                                <input
                                    value={eventForm.opponent}
                                    onChange={e => setEventForm({ ...eventForm, opponent: e.target.value })}
                                    className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                    placeholder="If available"
                                />
                            </div>
                            {editingEvent && eventForm.event_type === 'match' && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Result Summary</label>
                                    <input
                                        value={eventForm.result}
                                        onChange={e => setEventForm({ ...eventForm, result: e.target.value })}
                                        className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-2 text-white"
                                        placeholder="e.g. Win 3-1"
                                    />
                                </div>
                            )}
                            <div className="flex gap-2 justify-end mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowEventModal(false)}
                                    className="px-4 py-2 text-gray-300 hover:bg-gray-800 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-snapp-purple text-white rounded hover:bg-snapp-purple/80"
                                >
                                    {editingEvent ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Roster Tab
const RosterTab = ({ team, isCoach }) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchRoster = async () => {
        try {
            const response = await rosterAPI.getRoster(team.id);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to load roster:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoster();
    }, [team.id]);

    const handleKick = async (memberUserId, memberName) => {
        if (!window.confirm(`Are you sure you want to remove ${memberName} from the team? This action cannot be undone.`)) {
            return;
        }

        try {
            await rosterAPI.removeMember(team.id, memberUserId);
            toast({
                title: "Member removed",
                description: `${memberName} has been removed from the team.`,
            });
            fetchRoster(); // Refresh the list
        } catch (error) {
            const msg = error.response?.data?.detail || "Failed to remove member";
            toast({
                title: "Error",
                description: msg,
                variant: "destructive"
            });
        }
    };

    if (loading) return <div className="text-center py-20"><div className="w-8 h-8 border-2 border-snapp-blue border-t-transparent rounded-full animate-spin mx-auto"></div></div>;

    const players = members.filter(m => m.role === 'player');
    const coaches = members.filter(m => m.role === 'coach');

    const getPositionBadge = (pos) => {
        const colors = {
            'CB': 'bg-blue-600', 'S': 'bg-blue-600',
            'DE': 'bg-red-600', 'DT': 'bg-red-600',
            'MLB': 'bg-purple-600', 'OLB': 'bg-purple-600',
            'HB': 'bg-green-600', 'FB': 'bg-green-600',
            'WR': 'bg-orange-500', 'TE': 'bg-amber-500',
            'OG': 'bg-slate-600', 'OT': 'bg-slate-600', 'C': 'bg-slate-600',
            'QB': 'bg-yellow-500', 'K': 'bg-pink-600', 'P': 'bg-pink-600'
        };

        return (
            <Badge
                key={pos}
                className={`${colors[pos] || 'bg-gray-600'} text-white border-none px-2 py-0.5 rounded-md text-[9px] font-black tracking-wider uppercase`}
            >
                {pos}
            </Badge>
        );
    };

    const MemberCard = ({ member, colorClass }) => (
        <div className="relative group flex items-center gap-4 bg-[#1a1a1b]/60 backdrop-blur-xl p-4 rounded-[2rem] border border-white/5 shadow-lg active:scale-[0.98] transition-all">
            <div className="relative">
                <Avatar className="h-14 w-14 rounded-2xl border border-white/10 p-0.5">
                    <AvatarImage src={member.avatar_url} className="rounded-2xl" />
                    <AvatarFallback className={cn("text-white font-black rounded-2xl", colorClass)}>
                        {member.username.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                </Avatar>
                {member.is_captain && (
                    <div className="absolute -top-2 -right-2 bg-amber-500 p-1 rounded-lg border-2 border-[#1a1a1b] shadow-xl">
                        <Crown className="w-3 h-3 text-white fill-current" />
                    </div>
                )}
            </div>
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    {member.jersey_number && (
                        <span className="text-snapp-green font-black tabular-nums text-lg">#{member.jersey_number}</span>
                    )}
                    <p className="font-black text-white text-base leading-tight">{member.username}</p>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {member.positions && member.positions.length > 0
                        ? member.positions.map(pos => getPositionBadge(pos))
                        : <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{member.role === 'coach' ? 'Lead Coach' : 'Team Member'}</span>}
                </div>
            </div>
            {isCoach && (
                <button
                    onClick={() => handleKick(member.user_id, member.username)}
                    className="p-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-2xl transition-all active:scale-90"
                    title="Kick Member"
                >
                    <Trash2 className="w-5 h-5" />
                </button>
            )}
        </div>
    );

    return (
        <div className="space-y-12 animate-in slide-in-from-bottom-4 duration-500">
            {/* Coaches */}
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-snapp-blue/10 rounded-xl">
                        <Users className="w-5 h-5 text-snapp-blue" />
                    </div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('roster.coaches', 'Antrenörler')}</h3>
                    <div className="h-px flex-1 bg-white/5" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {coaches.map(coach => (
                        <MemberCard key={coach.id} member={coach} colorClass="bg-snapp-blue" />
                    ))}
                </div>
            </div>

            {/* Players */}
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-snapp-purple/10 rounded-xl">
                        <Users className="w-5 h-5 text-snapp-purple" />
                    </div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('roster.players', 'Oyuncular')}</h3>
                    <div className="h-px flex-1 bg-white/5" />
                </div>
                {players.length === 0 ? (
                    <div className="text-center py-10 bg-[#1a1a1b]/30 rounded-[2rem] border border-white/5 border-dashed">
                        <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">No players yet</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {players.map(player => (
                            <MemberCard key={player.id} member={player} colorClass="bg-snapp-purple" />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// PostCard Component (Simplified for Profile)
const PostCard = ({ post, allowViewAuthor }) => {
    const { t } = useTranslation();
    const [liked, setLiked] = useState(post.is_liked);
    const [likeCount, setLikeCount] = useState(post.like_count || 0);
    const [commentCount, setCommentCount] = useState(post.comment_count || 0);
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState(post.comments || []);
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const { user } = useAuth();
    const { toast } = useToast();

    const handleLike = async () => {
        const newLiked = !liked;
        const newCount = newLiked ? likeCount + 1 : likeCount - 1;
        setLiked(newLiked);
        setLikeCount(newCount);

        try {
            await socialAPI.toggleLike(post.id);
        } catch (error) {
            setLiked(!newLiked);
            setLikeCount(likeCount);
        }
    };

    const handleComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;

        setSubmittingComment(true);
        try {
            const response = await socialAPI.addComment(post.id, commentText);
            setComments([...comments, response.data]);
            setCommentCount(commentCount + 1);
            setCommentText('');
        } catch (error) {
            console.error('Failed to comment', error);
            toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Failed to post comment'
            });
        } finally {
            setSubmittingComment(false);
        }
    };

    return (
        <div className="bg-[#1a1a1b]/40 backdrop-blur-md border border-white/5 rounded-[2.5rem] mb-6 overflow-hidden shadow-xl">
            <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12 rounded-2xl border border-white/10 p-0.5">
                            <AvatarImage src={post.author?.avatar_url} className="rounded-2xl object-cover" />
                            <AvatarFallback className="bg-gradient-to-br from-snapp-purple to-snapp-blue text-white font-black rounded-2xl">
                                {post.author?.username?.substring(0, 2).toUpperCase() || 'TM'}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <div className="flex items-center gap-2">
                                <p className="text-base font-black text-white leading-none">
                                    {post.author?.username || 'Team Member'}
                                </p>
                                {allowViewAuthor && post.posted_by && (
                                    <Badge className="bg-white/10 text-gray-400 hover:bg-white/20 font-bold border-0 text-[9px] px-2 py-0">
                                        ADMIN
                                    </Badge>
                                )}
                            </div>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                                {format(parseISO(post.created_at), 'MMMM d, yyyy')}
                            </p>
                        </div>
                    </div>
                </div>

                {post.caption && (
                    <div className="mb-4">
                        <ExpandableText text={post.caption} className="text-[15px] leading-relaxed text-gray-200 font-medium" />
                    </div>
                )}

                {post.video?.file_url && (
                    <div className="rounded-[2rem] overflow-hidden bg-black mb-4 relative group shadow-2xl border border-white/5">
                        <SimpleVideoPlayer
                            src={post.video.file_url}
                            className="w-full aspect-[4/5] lg:aspect-video object-cover"
                            poster={post.video.thumbnail_url}
                        />
                    </div>
                )}

                {post.image_url && (
                    <div className="rounded-[2rem] overflow-hidden bg-black mb-4 border border-white/5 shadow-2xl">
                        <img
                            src={post.image_url}
                            alt="Post content"
                            className="w-full h-auto max-h-[500px] object-cover"
                        />
                    </div>
                )}

                <div className="flex items-center gap-6 pt-4 border-t border-white/5">
                    <button
                        onClick={handleLike}
                        className={cn(
                            "flex items-center gap-2 px-4 py-2 rounded-full transition-all active:scale-90",
                            liked ? "bg-pink-500/10 text-pink-500" : "bg-white/5 text-gray-400 hover:text-white"
                        )}
                    >
                        <Heart className={cn("w-5 h-5", liked ? "fill-current" : "")} />
                        <span className="text-sm font-black tabular-nums">{likeCount}</span>
                    </button>
                    <button
                        onClick={() => setShowComments(!showComments)}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-gray-400 hover:text-white transition-all active:scale-90"
                    >
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm font-black tabular-nums">{commentCount}</span>
                    </button>
                </div>

                {/* Comments Section */}
                {showComments && (
                    <div className="mt-6 space-y-4 animate-in slide-in-from-top duration-300">
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                            {comments.length === 0 ? (
                                <div className="text-center py-6 opacity-40">
                                    <MessageCircle className="w-8 h-8 mx-auto mb-2" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">No comments yet</p>
                                </div>
                            ) : (
                                comments.map(comment => (
                                    <div key={comment.id} className="flex gap-3 group">
                                        <Avatar className="h-8 w-8 rounded-xl ring-1 ring-white/10">
                                            <AvatarFallback className="bg-white/10 text-[10px] font-black rounded-xl">
                                                {comment.username?.substring(0, 2).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 bg-white/5 rounded-2xl p-3 border border-white/5">
                                            <div className="flex justify-between items-baseline mb-1">
                                                <span className="text-xs font-black text-white">{comment.username}</span>
                                                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-tighter">
                                                    {format(parseISO(comment.created_at), 'MMM d, p')}
                                                </span>
                                            </div>
                                            <p className="text-[13px] text-gray-400 leading-snug">{comment.text}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <form onSubmit={handleComment} className="flex gap-2 p-1.5 bg-black/40 rounded-2xl border border-white/5">
                            <input
                                type="text"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder={t('social.commentPlaceholder', 'Yorum yap...')}
                                className="flex-1 bg-transparent border-none px-4 py-2 text-sm text-white focus:ring-0 placeholder:text-gray-600 font-medium"
                            />
                            <button
                                type="submit"
                                disabled={submittingComment || !commentText.trim()}
                                className="bg-snapp-blue hover:bg-snapp-blue/90 disabled:opacity-50 text-white p-2.5 rounded-xl transition-all active:scale-95"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

// PostsTab is now integrated into OverviewTab

// Main TeamProfile Component
const TeamProfile = () => {
    const { t } = useTranslation();
    const { teamId } = useParams();
    const { user, updateProfile } = useAuth();
    const { toast } = useToast();
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const fileInputRef = useRef(null);

    const fetchProfile = async () => {
        try {
            const response = await teamProfileAPI.getProfile(teamId);
            setTeam(response.data);
        } catch (error) {
            console.error('Failed to load team profile:', error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Could not load team profile",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            // Upload image
            const uploadRes = await socialAPI.uploadImage(formData);
            const logoUrl = uploadRes.data.url;

            // Update profile
            await teamProfileAPI.updateProfile(teamId, { logo_url: logoUrl });

            toast({
                title: "Success",
                description: "Team logo updated",
            });
            fetchProfile();
        } catch (error) {
            console.error('Failed to upload logo:', error);
            toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Failed to update logo'
            });
        }
    };

    useEffect(() => {
        fetchProfile();
    }, [teamId]);


    const handleToggleFollow = async () => {
        try {
            const response = await socialAPI.toggleTeamFollow(teamId);
            setTeam(prev => ({
                ...prev,
                is_following: response.data.following
            }));
            toast({
                title: response.data.following ? "You are now following this team" : "You have unfollowed this team",
                description: response.data.following ? "Their posts will appear in your feed." : "Their posts will no longer appear in your feed."
            });
        } catch (error) {
            console.error("Failed to toggle follow:", error);
            toast({ variant: "destructive", title: "Action failed", description: "Could not update follow status." });
        }
    };

    const handleBecomeFan = async () => {
        try {
            // Update on backend
            await socialAPI.updateProfile({ favorite_team_id: teamId });

            // Update local user context
            updateProfile({ favorite_team_id: teamId });

            // Optimistically update team supporter count
            setTeam(prev => ({
                ...prev,
                supporters_count: (prev.supporters_count || 0) + 1
            }));

            toast({
                title: "Welcome to the family!",
                description: `You are now a supporter of ${team.name}`,
            });
        } catch (error) {
            console.error("Failed to become fan:", error);
            toast({
                variant: 'destructive',
                title: "Error",
                description: "Failed to set favorite team",
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#0a0a0b]">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!team) return <div>Team not found</div>;

    const canManageProfile = team.is_coach || team.is_captain || (team.permissions && team.permissions.includes('manage_posts')) || user?.role === 'admin';

    return (
        <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 animate-in fade-in duration-500">
            {/* Mobile Sticky Header */}
            <div className="lg:hidden p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-[100] border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h2 className="text-xl font-black text-white tracking-tight truncate max-w-[200px] uppercase">
                        {team.name}
                    </h2>
                </div>
                {team.plan === 'premium' && (
                    <div className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full flex items-center gap-1.5">
                        <Crown className="w-3 h-3 text-amber-500 fill-amber-500/20" />
                        <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">Premium</span>
                    </div>
                )}
            </div>

            {/* Banner Section */}
            <div className="relative mb-8">
                <div className="h-48 lg:h-64 bg-gradient-to-br from-snapp-purple/40 via-snapp-blue/40 to-black rounded-b-[3rem] lg:rounded-xl mb-4 overflow-hidden relative shadow-2xl">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
                    <div className="absolute top-0 right-0 w-64 h-64 bg-snapp-purple/20 blur-[100px] -mr-32 -mt-32" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-snapp-blue/20 blur-[100px] -ml-32 -mb-32" />
                </div>

                <div className="flex flex-col md:flex-row items-center md:items-end gap-6 px-6 -mt-20 lg:-mt-16 relative z-10">
                    <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-[2.5rem] bg-[#1a1a1b] border-4 border-[#0a0a0b] overflow-hidden shadow-2xl flex items-center justify-center relative group ring-4 ring-white/5">
                        {team.logo_url ? (
                            <img src={team.logo_url} alt={team.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="text-4xl lg:text-5xl font-black text-gray-500">{team.name.substring(0, 2).toUpperCase()}</div>
                        )}
                        {canManageProfile && (
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white"
                            >
                                <Camera className="w-8 h-8" />
                            </button>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={handleLogoUpload}
                        />
                    </div>

                    <div className="flex-1 text-center md:text-left">
                        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
                            <h1 className="text-3xl lg:text-4xl font-black text-white uppercase tracking-tight">{team.name}</h1>
                            <div className="flex items-center justify-center md:justify-start gap-3">
                                {team.division && (
                                    <Badge className={cn(
                                        "font-black border-none px-3 py-1.5 text-[10px] tracking-widest flex items-center gap-1.5 rounded-full transition-all duration-300",
                                        team.division === 'div_1'
                                            ? "bg-gradient-to-r from-yellow-600 to-yellow-400 text-black shadow-lg shadow-yellow-500/20"
                                            : "bg-gradient-to-r from-blue-700 to-blue-500 text-white shadow-lg shadow-blue-500/20"
                                    )}>
                                        {team.league_type === 'unilig' ? 'UNI' : 'PRO'} {team.division === 'div_1' ? '1' : '2'}
                                    </Badge>
                                )}

                                <Button
                                    size="sm"
                                    variant={team.is_following ? "secondary" : "default"}
                                    onClick={handleToggleFollow}
                                    className={cn(
                                        "h-9 px-6 rounded-full font-black text-[11px] uppercase tracking-wider transition-all active:scale-95 shadow-lg",
                                        team.is_following
                                            ? "bg-white/10 hover:bg-white/20 text-white border border-white/10"
                                            : "bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-blue-500/40"
                                    )}
                                >
                                    {team.is_following ? t('common.following', 'Takiptesin') : t('common.follow', 'Takip Et')}
                                </Button>
                            </div>
                        </div>

                        {/* Quick Stats Grid Mobile */}
                        <div className="grid grid-cols-2 gap-4 mt-6 md:hidden">
                            <div className="bg-white/5 border border-white/5 p-4 rounded-3xl text-center">
                                <div className="text-2xl font-black text-white tabular-nums">{team.member_count}</div>
                                <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1">{t('nav.roster', 'Kadro')}</div>
                            </div>
                            <div className="bg-white/5 border border-white/5 p-4 rounded-3xl text-center">
                                <div className="text-2xl font-black text-amber-500 tabular-nums">{team.supporters_count || 0}</div>
                                <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1">Sponsor</div>
                            </div>
                        </div>

                        {/* Desktop Stats */}
                        <p className="hidden md:flex text-gray-300 items-center gap-3 text-sm font-medium mt-2">
                            <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full border border-white/5 text-[12px] uppercase tracking-wider">
                                <Users className="w-3.5 h-3.5 text-blue-400" /> {team.member_count} Members
                            </span>
                            <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full border border-white/5 text-[12px] uppercase tracking-wider">
                                <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500/20" /> {team.supporters_count || 0} Supporters
                            </span>
                            {team.home_field && (
                                <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full border border-white/5 text-[12px] uppercase tracking-wider">
                                    <MapPin className="w-3.5 h-3.5 text-snapp-green" /> {team.home_field}
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <Tabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                isCoach={team.is_coach}
            />

            <div className="max-w-6xl mx-auto">
                {activeTab === 'overview' && (
                    <OverviewTab
                        team={team}
                        isCoach={team.is_coach}
                        canManageProfile={canManageProfile}
                        onUpdate={fetchProfile}
                    />
                )}
                {activeTab === 'calendar' && (
                    <CalendarTab
                        team={team}
                        events={team.upcoming_events || []}
                        isCoach={team.is_coach || canManageProfile}
                        onUpdate={fetchProfile}
                    />
                )}
                {activeTab === 'roster' && (
                    <RosterTab team={team} />
                )}
            </div>
        </div>
    );
};

export default TeamProfile;
